import React, { useCallback, useMemo, useState } from "react";
import { resolveTopicClusteringLens } from "@okf/ootils";

import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { PrioritiesLayoutV2 } from "@/components/analysisModules/TopicClusteringTreeViewV2/PrioritiesLayoutV2";
import { SignalCategoriesProvider } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";

import OverlayScrollbar from "@/components/UI/OverlayScrollbar";
import {
  WidgetEmptyState,
  WidgetLoadingState,
} from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../../../ReportLandingContext";

import { PrioritiesWidgetConfigForm } from "./PrioritiesWidgetConfigForm";
import { DEFAULTS, resolvePeriod } from "./settings";

/**
 * TempPriorities — the Priorities page (severity × momentum over the topic
 * tree) as a report widget, section by section.
 *
 * The page's PrioritiesLayoutV2 renders as is; what this widget adds is the
 * page's chrome, reduced to config: the period (the sidebar's dropdown),
 * the scoring grain (the Sub-topics / Topics switch, or a fixed one), the
 * layout (bubble chart or table), and an on/off per section — the trend
 * window pills, the zone filter bar, the act-first list. The page's signal
 * category cards are left out: they are to become a widget of their own.
 *
 * Scope: the report's filters plus the widget's own (`widgetFilters`, the
 * config panel's Filters section), straight through as the layout's
 * `historyFilters` — Priorities scores over the whole history within that
 * scope and slices it around the period itself, so the period is NOT a
 * filter here, exactly as on the page.
 *
 * Not carried over from the page: clicking a bubble or row (the page opens a
 * side panel with the sub-topic's observations).
 *
 * Public-report snapshot: the scorer's answer ({ scored, counts }) and the
 * period it was scored around are pushed to the report's capture store once
 * they are in, and `prefetchedData` replays them — no request is made, and
 * the grain switch and trend pills (each a new request) are left out.
 */

// The cell's height cap — the Annotation Explorer and Picked Annotations
// widgets' figure; past it the widget scrolls.
const WIDGET_MAX_HEIGHT = 500;

/**
 * SPLIT-MODE ONLY. This widget scores Findings (post-annotation topic
 * clustering) over the signals taxonomy and the dates-that-matter lens — none
 * of which a dual-agent or legacy tenant has, so it would render permanently
 * empty there.
 *
 * `!== "legacy"` is NOT sufficient: dual-agent tenants pass that check too. The
 * Dashboard gates its own Findings tab on `mode === "split"` for exactly this
 * reason (see SM_Dashboard/index.js) — same test here.
 */
export const optionDef = {
  display: "Priorities",
  icon: "Crosshair",
  desc: "The Priorities field or table, with each section on or off",
  isAvailableToAdd: ({ platformConfigs }) => getAnnoMode(platformConfigs) === "split",
};

export const Component = ({
  scoreGrain = DEFAULTS.scoreGrain,
  layout = DEFAULTS.layout,
  tableTab = DEFAULTS.tableTab,
  hiddenColumns = DEFAULTS.hiddenColumns,
  periodValue = DEFAULTS.periodValue,
  trendWindow = DEFAULTS.trendWindow,
  showTrendWindow = DEFAULTS.showTrendWindow,
  showZoneBar = DEFAULTS.showZoneBar,
  showActFirst = DEFAULTS.showActFirst,
  widgetFilters = [],
  activeFilters = [],
  widgetIdx = 0,
  id,
  prefetchedData,
}) => {
  const { updateWidgetDataStore } = useReportLandingContext() || {};
  const platformConfigs = useGetQueryData("platformConfigs");
  const allTpls = useGetQueryData("allTpls") || [];
  // Priorities reads the tenant's lens — the A/B lens switch belongs to the
  // Topics cards, not here (same as the page).
  const topicClusteringLens = resolveTopicClusteringLens({
    platformConfigs_ai: platformConfigs?.ai,
  });

  const { data: dateFilterOptions, isLoading: periodsFetching } =
    useGetDatesThatMatterFilterOptions({ enabled: !prefetchedData });
  const periodsLoading = !prefetchedData && periodsFetching;
  const period = useMemo(
    () =>
      prefetchedData
        ? prefetchedData.period
        : resolvePeriod(dateFilterOptions?.periods, periodValue),
    [prefetchedData, dateFilterOptions, periodValue],
  );

  const historyFilters = useMemo(() => [...activeFilters, ...widgetFilters], [activeFilters, widgetFilters]);

  const titleByContentType = useMemo(
    () =>
      Object.fromEntries(
        allTpls.map((t) => [
          t.kp_content_type,
          t.general?.content?.title || t.kp_content_type,
        ]),
      ),
    [allTpls],
  );

  // The tenant's category contract rides along with the counts payload; the
  // layout hands it up and every category-coloured piece below reads the
  // theme built from it (the page does the same).
  const [signalConfig, setSignalConfig] = useState({
    signalCategories: prefetchedData?.counts?.signalCategories || null,
    prioritiesCopy: prefetchedData?.counts?.prioritiesCopy || null,
  });
  const rememberCountsMeta = useCallback((payload) => {
    if (payload?.signalCategories)
      setSignalConfig({
        signalCategories: payload.signalCategories,
        prioritiesCopy: payload.prioritiesCopy || null,
      });
  }, []);
  const signalCategoryTheme = useMemo(
    () =>
      buildSignalCategoryTheme(signalConfig.signalCategories, {
        prioritiesCopy: signalConfig.prioritiesCopy,
      }),
    [signalConfig],
  );

  // Capture for the public snapshot: the scorer's answer plus the period it
  // was scored around. Of the counts payload only the category contract is
  // kept — the layout reads nothing else from it, and the rest is the whole
  // tree with stats.
  const rememberScored = useCallback(
    ({ scored, counts }) => {
      if (prefetchedData || !updateWidgetDataStore || !id) return;
      updateWidgetDataStore(id, {
        period,
        scored,
        counts: counts
          ? { signalCategories: counts.signalCategories, prioritiesCopy: counts.prioritiesCopy || null }
          : null,
      });
    },
    [prefetchedData, updateWidgetDataStore, id, period],
  );

  // One object for the frozen answer: the layout's hook reads it as its
  // data, and a fresh one each render would re-fire its counts effect.
  const prefetched = useMemo(
    () => (prefetchedData ? { scored: prefetchedData.scored, counts: prefetchedData.counts } : undefined),
    [prefetchedData],
  );

  // The layout owns the requests; it tells us when they settle.
  const [layoutLoading, setLayoutLoading] = useState(true);
  const loading = periodsLoading || (!!period && layoutLoading);
  useUpdateLoadedWidgetsTracker({
    status: loading ? "loading" : "success",
    widgetIdx,
  });

  if (periodsLoading) return <WidgetLoadingState />;
  if (!period)
    return (
      <WidgetEmptyState display="No periods to score — no dated annotations yet" />
    );

  // A frozen answer cannot be re-scored: the viewer's grain switch and the
  // trend pills stay off on the snapshot (the table tabs are fine — both
  // lists are in the answer).
  const viewerSwitchesGrain = scoreGrain === "both" && !prefetchedData;
  const viewerSwitchesTableTab = tableTab === "both";

  return (
    <SignalCategoriesProvider value={signalCategoryTheme}>
      {/* The page has the viewport; a widget has a grid cell. Same shell as
          the Picked Annotations widget: a capped height, and anything taller
          scrolls inside rather than stretching the row. Width is never
          scrolled — the zone cards wrap and the act-first list shrinks. */}
      <div
        style={{
          maxHeight: `${WIDGET_MAX_HEIGHT}px`,
          position: "relative",
          display: "flex",
          padding: "1.6rem",
          paddingBottom: 0,
        }}
      >
        <OverlayScrollbar style={{ flex: 1 }} overlay={false}>
          <div style={{ paddingBottom: "1.6rem" }}>
            <PrioritiesLayoutV2
              // A new grain, trend window or table list restarts the layout
              // on it — all three are local state there, seeded once.
              key={`${scoreGrain}:${trendWindow}:${tableTab}`}
              mode={layout}
              topicClusteringLens={topicClusteringLens}
              historyFilters={historyFilters}
              period={period}
              titleByContentType={titleByContentType}
              onCountsMeta={rememberCountsMeta}
              onLoadingChange={setLayoutLoading}
              onScored={rememberScored}
              prefetched={prefetched}
              initialScoreGrain={viewerSwitchesGrain ? "findings" : scoreGrain}
              initialTrendWindow={trendWindow}
              initialTableTab={viewerSwitchesTableTab ? "priorities" : tableTab}
              hiddenTableColumns={hiddenColumns}
              sections={{
                trendWindow: showTrendWindow && !prefetchedData,
                grainSwitch: viewerSwitchesGrain,
                zoneBar: showZoneBar,
                actFirst: showActFirst,
                tableTabs: viewerSwitchesTableTab,
              }}
              fillHeight={false}
            />
          </div>
        </OverlayScrollbar>
      </div>
    </SignalCategoriesProvider>
  );
};

export const ConfigForm = PrioritiesWidgetConfigForm;
